import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useSnackbar } from '../contexts/SnackbarContext.tsx';
import { useAuth } from '../contexts/AuthContext.tsx';
import { miscPurchasesApi, MiscPurchase } from '../services/miscPurchasesService.ts';

const emptyForm = {
  itemName: '',
  date: '',
  value: '',
  purchaserName: '',
  amountTakenFrom: '',
  cashAmount: '',
  reasonForPurchase: '',
};

const headerCellSx = {
  color: '#fff',
  fontWeight: 700,
  fontSize: '0.8rem',
  letterSpacing: '0.04em',
  textTransform: 'uppercase' as const,
  border: '2px solid #7f0000',
  py: 1.5,
  px: 1.5,
  whiteSpace: 'nowrap' as const,
};

const MiscPurchasesPage: React.FC = () => {
  const { showSuccess, showError } = useSnackbar();
  const { sessionToken } = useAuth();
  const [entries, setEntries] = useState<MiscPurchase[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<MiscPurchase | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'pending' | 'saving' | 'saved' | 'error'>('idle');
  const [autoSaveError, setAutoSaveError] = useState('');
  const autoSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
    };
  }, []);

  const fetchEntries = async () => {
    if (!sessionToken) return;
    setLoading(true);
    try {
      const res = await miscPurchasesApi.getAll({ page: 1, limit: 500 }, sessionToken);
      setEntries(res.data);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to load entries');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEntries();
  }, [sessionToken]); // eslint-disable-line react-hooks/exhaustive-deps

  const openAdd = () => {
    setEditingEntry(null);
    setForm({ ...emptyForm, date: new Date().toISOString().slice(0, 10) });
    setDialogOpen(true);
  };

  const openEdit = (entry: MiscPurchase) => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
      autoSaveTimeoutRef.current = null;
    }
    setAutoSaveStatus('idle');
    setAutoSaveError('');
    setEditingEntry(entry);
    setForm({
      itemName: entry.itemName,
      date: entry.date,
      value: entry.value.toString(),
      purchaserName: entry.purchaserName,
      amountTakenFrom: entry.amountTakenFrom,
      cashAmount: entry.cashAmount.toString(),
      reasonForPurchase: entry.reasonForPurchase,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id: number) => {
    if (!sessionToken || !window.confirm('Delete this entry?')) return;
    try {
      await miscPurchasesApi.remove(id, sessionToken);
      showSuccess('Entry deleted.');
      fetchEntries();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to delete entry');
    }
  };

  const validateForm = (f: typeof emptyForm): string | null => {
    if (!f.itemName.trim() || !f.date || !f.value || !f.purchaserName.trim() || !f.amountTakenFrom.trim() || !f.cashAmount || !f.reasonForPurchase.trim()) {
      return 'Please fill in all fields.';
    }
    if (isNaN(parseFloat(f.value)) || isNaN(parseFloat(f.cashAmount))) {
      return 'Value and Cash Amount must be valid numbers.';
    }
    return null;
  };

  const buildPayload = (f: typeof emptyForm) => ({
    itemName: f.itemName.trim(),
    date: f.date,
    value: parseFloat(f.value),
    purchaserName: f.purchaserName.trim(),
    amountTakenFrom: f.amountTakenFrom.trim(),
    cashAmount: parseFloat(f.cashAmount),
    reasonForPurchase: f.reasonForPurchase.trim(),
  });

  // Auto-saves ~900ms after the last change to any field, while editing an existing
  // entry. Adding a new entry still requires the explicit Add button.
  const performAutoSave = useCallback(
    async (id: number, snapshot: typeof emptyForm) => {
      if (!sessionToken) return;
      const validationError = validateForm(snapshot);
      if (validationError) {
        setAutoSaveStatus('error');
        setAutoSaveError(validationError);
        return;
      }
      setAutoSaveStatus('saving');
      try {
        await miscPurchasesApi.update(id, buildPayload(snapshot), sessionToken);
        setAutoSaveStatus('saved');
        fetchEntries();
      } catch (err) {
        setAutoSaveStatus('error');
        setAutoSaveError(err instanceof Error ? err.message : 'Failed to save changes');
      }
    },
    [sessionToken]
  );

  const handleFieldChange = (field: keyof typeof emptyForm, value: string) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (editingEntry) {
        if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
        setAutoSaveStatus('pending');
        autoSaveTimeoutRef.current = setTimeout(() => {
          autoSaveTimeoutRef.current = null;
          performAutoSave(editingEntry.id, next);
        }, 900);
      }
      return next;
    });
  };

  const handleCloseDialog = async () => {
    if (autoSaveTimeoutRef.current && editingEntry) {
      clearTimeout(autoSaveTimeoutRef.current);
      autoSaveTimeoutRef.current = null;
      await performAutoSave(editingEntry.id, form);
    }
    setDialogOpen(false);
  };

  const handleSave = async () => {
    if (!sessionToken) return;
    const validationError = validateForm(form);
    if (validationError) {
      showError(validationError);
      return;
    }

    setSaving(true);
    try {
      const payload = buildPayload(form);
      if (editingEntry) {
        await miscPurchasesApi.update(editingEntry.id, payload, sessionToken);
        showSuccess('Entry updated.');
      } else {
        await miscPurchasesApi.create(payload, sessionToken);
        showSuccess('Entry added.');
      }
      setDialogOpen(false);
      fetchEntries();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to save entry');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (d: string) =>
    d ? new Date(d + 'T00:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

  const totalValue = entries.reduce((s, e) => s + Number(e.value), 0);
  const totalCash = entries.reduce((s, e) => s + Number(e.cashAmount), 0);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h6" sx={{ color: "#7f0000", fontWeight: 700, flex: "0 0 auto", whiteSpace: "nowrap" }}>
          Date: {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
        </Typography>
        <Typography variant="h4" component="h1" sx={{ flex: 1, textAlign: 'center', fontWeight: 700, textTransform: 'uppercase' }}>
          Misc. Purchases
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openAdd}
          sx={{ backgroundColor: '#b71c1c', '&:hover': { backgroundColor: '#7f0000' } }}
        >
          Add Entry
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {entries.length} entr{entries.length !== 1 ? 'ies' : 'y'}
        {entries.length > 0 && ` · Total Value: $${totalValue.toFixed(2)} · Total Cash: $${totalCash.toFixed(2)}`}
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ width: '100%', overflow: 'hidden', border: '2px solid #9e9e9e' }}>
          <TableContainer>
            <Table sx={{ borderCollapse: 'collapse', '& td': { border: '2px solid #9e9e9e', py: 1.25, px: 1.5 } }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#b71c1c' }}>
                  <TableCell sx={headerCellSx}>ID</TableCell>
                  <TableCell sx={headerCellSx}>Name of Item</TableCell>
                  <TableCell sx={headerCellSx}>Date</TableCell>
                  <TableCell align="right" sx={headerCellSx}>Value</TableCell>
                  <TableCell sx={headerCellSx}>Purchaser Name</TableCell>
                  <TableCell sx={headerCellSx}>Amount Taken From</TableCell>
                  <TableCell align="right" sx={headerCellSx}>Cash Amount</TableCell>
                  <TableCell sx={headerCellSx}>Reason for Purchase</TableCell>
                  <TableCell align="center" sx={headerCellSx}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 5, border: '2px solid #9e9e9e', color: 'text.secondary' }}>
                      No entries yet. Click "Add Entry" to get started.
                    </TableCell>
                  </TableRow>
                )}
                {entries.map((entry, idx) => (
                  <TableRow
                    key={entry.id}
                    sx={{
                      backgroundColor: idx % 2 === 0 ? '#fff' : '#fdf5f5',
                      '&:hover': { backgroundColor: '#fce4e4' },
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">{entry.id}</Typography>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{entry.itemName}</TableCell>
                    <TableCell>{formatDate(entry.date)}</TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="600">${Number(entry.value).toFixed(2)}</Typography>
                    </TableCell>
                    <TableCell>{entry.purchaserName}</TableCell>
                    <TableCell>{entry.amountTakenFrom}</TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="600">${Number(entry.cashAmount).toFixed(2)}</Typography>
                    </TableCell>
                    <TableCell>{entry.reasonForPurchase}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Edit">
                        <IconButton size="small" color="primary" onClick={() => openEdit(entry)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => handleDelete(entry.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {entries.length > 0 && (
                  <TableRow sx={{
                    backgroundColor: '#b71c1c',
                    '& td': { border: '2px solid #7f0000', py: 1.25, px: 1.5, color: '#fff', fontWeight: 700 },
                  }}>
                    <TableCell colSpan={3}>TOTAL</TableCell>
                    <TableCell align="right">${totalValue.toFixed(2)}</TableCell>
                    <TableCell colSpan={2} />
                    <TableCell align="right">${totalCash.toFixed(2)}</TableCell>
                    <TableCell colSpan={2} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingEntry ? 'Edit Entry' : 'Add Misc. Purchase'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Name of Item"
            fullWidth
            value={form.itemName}
            onChange={e => handleFieldChange('itemName', e.target.value)}
          />
          <TextField
            label="Date"
            type="date"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={form.date}
            onChange={e => handleFieldChange('date', e.target.value)}
          />
          <TextField
            label="Value"
            type="number"
            fullWidth
            inputProps={{ min: 0, step: 0.01 }}
            InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>$</Typography> }}
            value={form.value}
            onChange={e => handleFieldChange('value', e.target.value)}
          />
          <TextField
            label="Purchaser Name"
            fullWidth
            value={form.purchaserName}
            onChange={e => handleFieldChange('purchaserName', e.target.value)}
          />
          <TextField
            label="Amount Taken From"
            fullWidth
            placeholder="e.g. Petty Cash, Register 1"
            value={form.amountTakenFrom}
            onChange={e => handleFieldChange('amountTakenFrom', e.target.value)}
          />
          <TextField
            label="Cash Amount"
            type="number"
            fullWidth
            inputProps={{ min: 0, step: 0.01 }}
            InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>$</Typography> }}
            value={form.cashAmount}
            onChange={e => handleFieldChange('cashAmount', e.target.value)}
          />
          <TextField
            label="Reason for Purchase"
            fullWidth
            multiline
            minRows={2}
            value={form.reasonForPurchase}
            onChange={e => handleFieldChange('reasonForPurchase', e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={editingEntry ? { justifyContent: 'space-between' } : undefined}>
          {editingEntry ? (
            <>
              <Typography
                variant="body2"
                color={autoSaveStatus === 'error' ? 'error' : 'text.secondary'}
                sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 1 }}
              >
                {autoSaveStatus === 'saving' && (<><CircularProgress size={14} />Saving…</>)}
                {autoSaveStatus === 'pending' && 'Unsaved changes…'}
                {autoSaveStatus === 'saved' && 'All changes saved'}
                {autoSaveStatus === 'error' && (autoSaveError || 'Failed to save changes')}
              </Typography>
              <Button onClick={handleCloseDialog} variant="contained" disabled={autoSaveStatus === 'saving'}
                sx={{ backgroundColor: '#b71c1c', '&:hover': { backgroundColor: '#7f0000' } }}>
                Close
              </Button>
            </>
          ) : (
            <>
              <Button onClick={handleCloseDialog} disabled={saving}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
                sx={{ backgroundColor: '#b71c1c', '&:hover': { backgroundColor: '#7f0000' } }}
              >
                {saving ? <CircularProgress size={20} color="inherit" /> : 'Add'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MiscPurchasesPage;
